import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '../../contexts/AuthContext';
import { orderService } from '../../services';
import toast from 'react-hot-toast';

const OrderHistory = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        try {
            setLoading(true);
            // Simulamos datos de pedidos
            const mockOrders = [
                {
                    id: 'ORD-001',
                    date: new Date('2024-01-15'),
                    status: 'delivered',
                    total: 2499.99,
                    items: [
                        {
                            id: 1,
                            name: 'Laptop Gaming ASUS ROG',
                            quantity: 1,
                            price: 2499.99,
                            image: '/api/placeholder/100/100'
                        }
                    ]
                },
                {
                    id: 'ORD-002',
                    date: new Date('2024-01-20'),
                    status: 'shipped',
                    total: 1899.99,
                    items: [
                        {
                            id: 2,
                            name: 'Laptop HP Pavilion',
                            quantity: 1,
                            price: 1899.99,
                            image: '/api/placeholder/100/100'
                        }
                    ]
                },
                {
                    id: 'ORD-003',
                    date: new Date('2024-01-25'),
                    status: 'processing',
                    total: 3299.99,
                    items: [
                        {
                            id: 3,
                            name: 'MacBook Pro M3',
                            quantity: 1,
                            price: 3299.99,
                            image: '/api/placeholder/100/100'
                        }
                    ]
                }
            ];
            setOrders(mockOrders);
        } catch (error) {
            console.error('Error cargando pedidos:', error);
            toast.error('Error al cargar el historial de pedidos');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(price);
    };

    const getStatusSeverity = (status) => {
        switch (status) {
            case 'delivered':
                return 'success';
            case 'shipped':
                return 'info';
            case 'processing':
                return 'warning';
            case 'cancelled':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'delivered':
                return 'Entregado';
            case 'shipped':
                return 'Enviado';
            case 'processing':
                return 'Procesando';
            case 'cancelled':
                return 'Cancelado';
            default:
                return 'Desconocido';
        }
    };

    const statusBodyTemplate = (rowData) => {
        return (
            <Badge
                value={getStatusLabel(rowData.status)}
                severity={getStatusSeverity(rowData.status)}
            />
        );
    };

    const dateBodyTemplate = (rowData) => {
        return rowData.date.toLocaleDateString('es-PE');
    };

    const priceBodyTemplate = (rowData) => {
        return formatPrice(rowData.total);
    };

    const actionsBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-eye"
                    className="p-button-outlined p-button-sm"
                    onClick={() => viewOrderDetails(rowData)}
                    tooltip="Ver detalles"
                />
                {rowData.status === 'delivered' && (
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined p-button-sm"
                        onClick={() => reorderItems(rowData)}
                        tooltip="Volver a comprar"
                    />
                )}
            </div>
        );
    };

    const viewOrderDetails = (order) => {
        setSelectedOrder(order);
        setOrderDetailVisible(true);
    };

    const reorderItems = (order) => {
        toast.info('Función de recompra próximamente disponible');
    };

    const orderDetailFooter = (
        <div>
            <Button
                label="Cerrar"
                icon="pi pi-times"
                className="p-button-outlined"
                onClick={() => setOrderDetailVisible(false)}
            />
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="order-history">
            <Card>
                <div className="flex align-items-center justify-content-between mb-4">
                    <h2 className="text-2xl font-bold text-900 m-0">
                        Historial de Pedidos
                    </h2>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-outlined"
                        onClick={loadOrders}
                        tooltip="Actualizar"
                    />
                </div>

                {orders.length === 0 ? (
                    <div className="text-center py-6">
                        <i className="pi pi-shopping-bag text-6xl text-400 mb-3"></i>
                        <h3 className="text-xl text-600 mb-2">No tienes pedidos aún</h3>
                        <p className="text-600 mb-4">
                            Cuando realices tu primera compra, aparecerá aquí
                        </p>
                        <Button
                            label="Explorar Productos"
                            icon="pi pi-shopping-cart"
                            onClick={() => window.location.href = '/catalogo'}
                        />
                    </div>
                ) : (
                    <DataTable
                        value={orders}
                        paginator
                        rows={10}
                        className="p-datatable-sm"
                        emptyMessage="No se encontraron pedidos"
                    >
                        <Column
                            field="id"
                            header="Número de Pedido"
                            sortable
                        />
                        <Column
                            field="date"
                            header="Fecha"
                            body={dateBodyTemplate}
                            sortable
                        />
                        <Column
                            field="status"
                            header="Estado"
                            body={statusBodyTemplate}
                            sortable
                        />
                        <Column
                            field="total"
                            header="Total"
                            body={priceBodyTemplate}
                            sortable
                        />
                        <Column
                            header="Acciones"
                            body={actionsBodyTemplate}
                            style={{ width: '120px' }}
                        />
                    </DataTable>
                )}
            </Card>

            {/* Dialog de detalles del pedido */}
            <Dialog
                header={`Detalles del Pedido ${selectedOrder?.id}`}
                visible={orderDetailVisible}
                style={{ width: '50vw' }}
                footer={orderDetailFooter}
                onHide={() => setOrderDetailVisible(false)}
            >
                {selectedOrder && (
                    <div>
                        <div className="grid mb-4">
                            <div className="col-6">
                                <h4 className="text-900 mb-2">Información del Pedido</h4>
                                <p><strong>Número:</strong> {selectedOrder.id}</p>
                                <p><strong>Fecha:</strong> {selectedOrder.date.toLocaleDateString('es-PE')}</p>
                                <p><strong>Estado:</strong> 
                                    <Badge
                                        value={getStatusLabel(selectedOrder.status)}
                                        severity={getStatusSeverity(selectedOrder.status)}
                                        className="ml-2"
                                    />
                                </p>
                            </div>
                            <div className="col-6">
                                <h4 className="text-900 mb-2">Resumen</h4>
                                <p><strong>Subtotal:</strong> {formatPrice(selectedOrder.total * 0.82)}</p>
                                <p><strong>IGV (18%):</strong> {formatPrice(selectedOrder.total * 0.18)}</p>
                                <p><strong>Total:</strong> {formatPrice(selectedOrder.total)}</p>
                            </div>
                        </div>

                        <Divider />

                        <h4 className="text-900 mb-3">Productos</h4>
                        {selectedOrder.items.map((item, index) => (
                            <div key={index} className="flex align-items-center p-3 border-1 surface-border border-round mb-3">
                                <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-4rem h-4rem border-round mr-3"
                                />
                                <div className="flex-1">
                                    <h5 className="text-900 mb-1">{item.name}</h5>
                                    <p className="text-600 m-0">Cantidad: {item.quantity}</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-900">
                                        {formatPrice(item.price)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default OrderHistory;
